<template>
  <div class="operation-input">
    <h3>选择处理方式</h3>

    <!-- 预设操作 -->
    <div class="preset-operations">
      <h4>预设操作</h4>
      <div class="preset-grid">
        <button
          v-for="preset in presets"
          :key="preset.id"
          :class="['preset-btn', { active: selectedPreset === preset.id }]"
          @click="selectPreset(preset)"
        >
          <div class="preset-icon">{{ preset.icon }}</div>
          <div class="preset-info">
            <div class="preset-name">{{ preset.name }}</div>
            <div class="preset-desc">{{ preset.description }}</div>
          </div>
        </button>
      </div>
    </div>

    <!-- 自定义操作 -->
    <div class="custom-operation">
      <h4>自定义操作描述</h4>
      <textarea
        v-model="customText"
        class="input textarea"
        placeholder="描述你希望对图片进行的操作，比如：将这张照片转换成卡通风格..."
        @input="updateOperation"
      ></textarea>
    </div>

    <!-- 当前选择的操作显示 -->
    <div v-if="currentOperation" class="current-operation">
      <h4>当前操作</h4>
      <div class="operation-preview">
        <span v-if="selectedPreset" class="operation-type">预设：{{ getCurrentPreset()?.name }}</span>
        <span v-else class="operation-type">自定义操作</span>
        <p class="operation-text">{{ currentOperation }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OperationInput',
  emits: ['update:operation'],
  data () {
    return {
      selectedPreset: null,
      customText: '',
      presets: [
        {
          id: 'figure',
          name: '手办化',
          icon: '🎎',
          description: '将照片制作成精美的手办模型',
          prompt: 'Please turn this photo into a figure. Behind it, there should be a packaging box with a large clear front window, printed character artwork, the product name, logo, barcode, a small specs or authenticity panel, and a handwriten price tag sticker on one corner. There is also a computer monitor screen at the back, showing the design sketch of the figure. In front of the box, on a round plastic base, place the figure version of the photo I gave you, and the figure must be three-dimensional. I\'d like the PVC material to be clearly represented. It would be even better if the background is indoors.'
        },
        {
          id: 'cartoon',
          name: '卡通化',
          icon: '🎨',
          description: '转换成可爱的卡通风格',
          prompt: 'Transform this photo into a cute cartoon style illustration with vibrant colors and simplified features.'
        },
        {
          id: 'anime',
          name: '动漫风格',
          icon: '🌸',
          description: '制作成日式动漫角色',
          prompt: 'Convert this photo into Japanese anime style character with large expressive eyes, detailed hair, and anime art style.'
        },
        {
          id: 'oil_painting',
          name: '油画风格',
          icon: '🖼️',
          description: '转换成古典油画作品',
          prompt: 'Transform this photo into a classical oil painting with rich textures, warm colors, and artistic brush strokes.'
        },
        {
          id: 'pixel_art',
          name: '像素艺术',
          icon: '🕹️',
          description: '制作成复古像素游戏风格',
          prompt: 'Convert this photo into retro pixel art style like classic video games with limited color palette and blocky appearance.'
        },
        {
          id: 'watercolor',
          name: '水彩画',
          icon: '💧',
          description: '制作成清新水彩画风格',
          prompt: 'Transform this photo into a beautiful watercolor painting with soft colors, flowing textures, and artistic transparency effects.'
        }
      ]
    }
  },
  computed: {
    currentOperation () {
      if (this.selectedPreset) {
        const preset = this.getCurrentPreset()
        return preset ? preset.prompt : ''
      }
      return this.customText.trim()
    }
  },
  methods: {
    selectPreset (preset) {
      if (this.selectedPreset === preset.id) {
        // 取消选择
        this.selectedPreset = null
      } else {
        this.selectedPreset = preset.id
        this.customText = '' // 清空自定义文本
      }
      this.updateOperation()
    },
    updateOperation () {
      this.$emit('update:operation', this.currentOperation)
    },
    getCurrentPreset () {
      return this.presets.find(p => p.id === this.selectedPreset)
    }
  },
  watch: {
    customText () {
      if (this.customText.trim()) {
        this.selectedPreset = null // 有自定义文本时取消预设选择
      }
    }
  }
}
</script>

<style scoped>
.operation-input h3 {
  margin-bottom: 24px;
  color: #2c3e50;
  font-size: 24px;
}

.operation-input h4 {
  margin-bottom: 16px;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.preset-operations {
  margin-bottom: 32px;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}

.preset-btn {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
}

.preset-btn:hover {
  border-color: #74b9ff;
  box-shadow: 0 4px 12px rgba(116, 185, 255, 0.15);
  transform: translateY(-2px);
}

.preset-btn.active {
  border-color: #74b9ff;
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  color: white;
}

.preset-icon {
  font-size: 32px;
  margin-right: 16px;
  flex-shrink: 0;
}

.preset-info {
  flex: 1;
}

.preset-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.preset-desc {
  font-size: 14px;
  opacity: 0.8;
}

.custom-operation {
  margin-bottom: 24px;
}

.current-operation {
  padding: 20px;
  background: rgba(116, 185, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(116, 185, 255, 0.2);
}

.operation-preview {
  margin-top: 8px;
}

.operation-type {
  display: inline-block;
  background: #74b9ff;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
}

.operation-text {
  color: #495057;
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 768px) {
  .preset-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .preset-btn {
    padding: 12px;
  }

  .preset-icon {
    font-size: 24px;
    margin-right: 12px;
  }
}
</style>
